import { TypographyMuted } from "@/components/typography/Muted";
import { TypographySM } from "@/components/typography/SM";
import { Button } from "@/components/ui/button";
import Dialog from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table } from "@tanstack/react-table";
import { Dispatch, SetStateAction, useState } from "react";
import { AppSessionsTableType } from "../types/appSession.type";
import { exportAsCSV } from "../utils/exportAppSessionDataAsCSV";

interface Props<TData> {
  table: Table<TData>;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  hasFilteredData: boolean;
}

export function DownloadAnalyticsDataModal<TData extends AppSessionsTableType>({
  table,
  onOpenChange,
  hasFilteredData
}: Props<TData>) {
  const [useFilteredData, setUseFilteredData] = useState(true);
  const [fileName, setFileName] = useState("");

  function handleDownload() {
    const data = useFilteredData
      ? table.getFilteredRowModel().rows
      : table.getPreFilteredRowModel().rows;

    exportAsCSV(data, fileName).then(() => onOpenChange(false));
  }

  return (
    <Dialog.Root open onOpenChange={onOpenChange}>
      <Dialog.Content className="dark:bg-brand-gray-900 max-w-2xl">
        <Dialog.Header className="space-y-2">
          <Dialog.Title className="leading-5 text-brand-gray-800 dark:text-brand-gray-100">
            Download analytics data
          </Dialog.Title>
          <Dialog.Description className="text-brand-gray-700 dark:text-brand-gray-300">
            Download the analytics data as a CSV file.
          </Dialog.Description>
        </Dialog.Header>
        <div className="flex flex-col items-start mt-4">
          {hasFilteredData && (
            <div className="grid grid-cols-2 mb-8">
              <TypographySM>
                Save the analytics data after applying your selected filters.
              </TypographySM>
              <Button
                variant="outline"
                onClick={() => setUseFilteredData((p) => !p)}
                className="w-fit justify-self-end"
              >
                <input
                  type="checkbox"
                  checked={useFilteredData}
                  className="mr-2 cursor-pointer"
                />
                Save filtered data
              </Button>
            </div>
          )}

          <Label className="mb-2">File name</Label>
          <div className="w-full flex gap-x-2 items-center">
            <Input
              value={fileName}
              placeholder="analytics_data"
              type="text"
              onChange={(e) => setFileName(e.target.value)}
              className="dark:bg-brand-gray-800 placeholder:italic"
            />
            <TypographyMuted>.csv</TypographyMuted>
          </div>
        </div>
        <Dialog.Footer className="mt-8">
          <Dialog.Close asChild>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Dialog.Close>
          <Button onClick={handleDownload} disabled={!fileName}>
            Download CSV
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
